<script setup lang="ts">
  import { onMounted } from 'vue';
  import { FormInput, PositionUserSelector, SelectInput, SimpleLoading } from '@/shared/ui';
  import { toNumberOrUndefined } from '@/shared/lib';
  import TrainingScheduledDateField from '../TrainingScheduledDateField.vue';
  import TrainingNotesField from '../TrainingNotesField.vue';
  import OpenTrainingCertificateSection from './OpenTrainingCertificateSection.vue';
  import OpenTrainingInstructorSection from './OpenTrainingInstructorSection.vue';
  import type { useOpenTrainingForm } from '../../model/useOpenTrainingForm';

  const props = defineProps<{
    form: ReturnType<typeof useOpenTrainingForm>;
  }>();

  onMounted(async () => {
    await props.form.load();
  });
</script>

<template>
  <div
    v-if="form.loading.value"
    data-testid="open-training-form-loading"
    class="flex w-full justify-center py-12"
  >
    <SimpleLoading />
  </div>

  <template v-else>
    <div class="flex flex-col gap-1.5 border-b border-gray-02 px-6 py-8">
      <FormInput
        id="open-training-title"
        :model-value="form.title.value"
        label="Título do Treinamento:"
        label-class="text-sm font-semibold text-gray-07"
        type="text"
        placeholder="Escreva um título para identificar o treinamento"
        @update:model-value="form.title.value = String($event)"
      />
    </div>

    <div class="flex flex-col gap-3.5 border-b border-gray-02 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Programação do Treinamento</span>
      <div class="grid grid-cols-[304px_348px_180px] gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            id="open-training-scheduled-by-member-label"
            class="text-sm font-semibold text-gray-07"
          >Responsável pela Programação:</span>
          <SelectInput
            :model-value="form.scheduledByMemberId.value ?? ''"
            :options="form.memberOptions.value"
            label="Selecione um colaborador"
            aria-labelledby="open-training-scheduled-by-member-label"
            @update:model-value="form.setScheduledByMemberId(toNumberOrUndefined($event) ?? null)"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="open-training-scheduled-by-position-label"
            class="text-sm font-semibold text-gray-07"
          >Cargo:</span>
          <SelectInput
            :model-value="form.scheduledByPositionId.value ?? ''"
            :options="form.scheduledByPositions.value"
            label="Selecione o cargo relacionado ao treinamento"
            aria-labelledby="open-training-scheduled-by-position-label"
            @update:model-value="form.scheduledByPositionId.value = toNumberOrUndefined($event) ?? null"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="open-training-scheduled-date-label"
            class="text-sm font-semibold text-gray-07"
          >Data da programação:</span>
          <TrainingScheduledDateField
            :model-value="form.scheduledDate.value"
            testid="open-training-scheduled-date-trigger"
            labelledby="open-training-scheduled-date-label"
            @update:model-value="form.scheduledDate.value = $event"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Selecione os colaboradores que deverão passar pelo treinamento:</span>
      <PositionUserSelector
        data-testid="open-training-collaborators-selector"
        :positions="form.collaborators.positions.value"
        :users="form.collaborators.users.value"
        :selected-positions="form.collaborators.selectedPositions.value"
        :selected-users="form.collaborators.selectedUsers.value"
        :positions-selected-count="form.collaborators.positionsSelectedCount.value"
        :users-selected-count="form.collaborators.usersSelectedCount.value"
        @update:selected-positions="form.collaborators.selectedPositions.value = $event"
        @update:selected-users="form.collaborators.selectedUsers.value = $event"
      />
    </div>

    <OpenTrainingInstructorSection
      :instructor="form.instructor"
      :members="form.members.value"
      :member-options="form.memberOptions.value"
    />

    <OpenTrainingCertificateSection
      :certificate-required="form.certificateRequired.value"
      :certificate-type="form.certificateType.value"
      :workload-hours="form.workloadHours.value"
      :enabled="form.certificateFieldsEnabled.value"
      @update:certificate-required="form.setCertificateRequired($event)"
      @update:certificate-type="form.certificateType.value = $event"
      @update:workload-hours="form.workloadHours.value = $event ?? 0"
    />

    <TrainingNotesField
      :model-value="form.notes.value"
      testid="open-training-notes-input"
      @update:model-value="form.notes.value = $event"
    />
  </template>
</template>
